import {
  Box, Button, Flex, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td,
  Text, Th, Thead, Tr, useColorModeValue, useDisclosure, useToast, FormControl,
  FormLabel, VStack, Badge, Spinner, HStack, Image, Switch,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper, InputGroup, InputRightElement,
  Checkbox, Wrap, WrapItem, Tag, TagLabel, TagCloseButton,
  Textarea,
} from '@chakra-ui/react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import * as React from 'react';
import {
  MdAdd, MdEdit, MdDelete, MdImage, MdCameraAlt, MdSearch,
} from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import {
  fetchPoojas, createPooja, updatePooja, deletePooja, togglePoojaStatus,
} from '../../../../Service/PoojaService';

const columnHelper = createColumnHelper();

// ── All 6 built-in booking types ────────────────────────────────────────────
const BOOKING_TYPES = ['INDIVIDUAL', 'FAMILY', 'ABHISHEKAM', 'HOMAM', 'GHO_POOJA', 'WHOLE_PACKAGE'];

// Booking types that cannot be combined with anything else.
// Selecting one of these clears every other selection (including custom
// types), and disables the other checkboxes until it is unchecked again.
const EXCLUSIVE_TYPES = ['WHOLE_PACKAGE'];

// Badge colour per built-in type
const TYPE_COLOR = {
  INDIVIDUAL:    'blue',
  FAMILY:        'purple',
  ABHISHEKAM:    'orange',
  HOMAM:         'red',
  GHO_POOJA:     'teal',
  WHOLE_PACKAGE: 'pink',
};

// Friendly label, e.g. GHO_POOJA -> "Gho Pooja"
const formatTypeLabel = (type) =>
  type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

// ── Price field config — one entry per built-in type ───────────────────────
const PRICE_FIELDS = [
  { type: 'INDIVIDUAL',     field: 'individualPrice',    label: 'Individual Price (₹)'    },
  { type: 'FAMILY',         field: 'familyPrice',        label: 'Family Price (₹)'        },
  { type: 'ABHISHEKAM',     field: 'abhishekamPrice',    label: 'Abhishekam Price (₹)'    },
  { type: 'HOMAM',          field: 'homamPrice',         label: 'Homam Price (₹)'         },
  { type: 'GHO_POOJA',      field: 'ghoPoojaPrice',      label: 'Gho Pooja Price (₹)'     },
  { type: 'WHOLE_PACKAGE',  field: 'wholePackagePrice',  label: 'Whole Package Price (₹)' },
];

// ── Member-count field config — one entry per built-in type ────────────────
const MEMBER_FIELDS = [
  { type: 'INDIVIDUAL',     field: 'individualMaxMembers',    label: 'Individual Max Members',    max: 100 },
  { type: 'FAMILY',         field: 'familyMaxMembers',        label: 'Family Max Members',         max: 200 },
  { type: 'ABHISHEKAM',     field: 'abhishekamMaxMembers',    label: 'Abhishekam Max Members',     max: 50  },
  { type: 'HOMAM',          field: 'homamMaxMembers',         label: 'Homam Max Members',          max: 50  },
  { type: 'GHO_POOJA',      field: 'ghoPoojaMaxMembers',      label: 'Gho Pooja Max Members',      max: 50  },
  { type: 'WHOLE_PACKAGE',  field: 'wholePackageMaxMembers',  label: 'Whole Package Max Members',  max: 200 },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ── Register a numeric px Size format (default Quill only ships
//    small/normal/large/huge — this swaps it for actual point sizes) ──────
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '40px', '48px'];
Quill.register(Size, true);

// ── Shared rich text editor config — reuse for any future ReactQuill field ─
const QUILL_TOOLBAR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link',
];

const DEFAULT_AVAILABILITY = {
  type: 'daily',
  weeklyDays: [],
  monthlyWeeks: [1],
  monthlyWeekdays: [1],
  monthlyDay: 1,
  monthlyNth: 1,
  monthlyWeekday: 1,
  specialDates: [],
  specialDateInput: '',
  maxBookingsPerDate: 1,
  excludedDates: [],
};

const DEFAULT_FORM = {
  poojaName: '',
  description: '',
  individualPrice: 0,
  familyPrice: 0,
  abhishekamPrice: 0,
  homamPrice: 0,
  ghoPoojaPrice: 0,
  wholePackagePrice: 0,
  homamName: '',
  bookingTypes: ['INDIVIDUAL', 'FAMILY'],
  individualMaxMembers: 30,
  familyMaxMembers: 60,
  abhishekamMaxMembers: 10,
  homamMaxMembers: 10,
  ghoPoojaMaxMembers: 10,
  wholePackageMaxMembers: 60,
  durationMinutes: 60,
  imageUrl: '',
  benefits: [],
  poojaItems: [],
  // Per-pooja custom booking types: [{ key, label, price, maxMembers }]
  customTypes: [],
  // Free-text note shown to devotees on the booking page (optional)
  noteText: '',
  availability: { ...DEFAULT_AVAILABILITY },
  isActive: true,
};

export default function PoojaTable() {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [imageUploading, setImageUploading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterActive, setFilterActive] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 10;

  const [formData, setFormData] = React.useState({ ...DEFAULT_FORM });
  const [editingRow, setEditingRow] = React.useState(null);
  const [selectedImageFile, setSelectedImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState('');
  const [newBenefit, setNewBenefit] = React.useState('');
  const [newItem, setNewItem] = React.useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deletingId, setDeletingId] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // ── Data loading ─────────────────────────────────────────────────────────
  const loadPoojas = React.useCallback(async () => {
    setLoading(true);
    const activeFilter = filterActive === 'active' ? true : filterActive === 'inactive' ? false : null;
    const result = await fetchPoojas({ search, activeOnly: activeFilter });
    if (result.success) setData(result.data);
    setLoading(false);
  }, [search, filterActive]);

  React.useEffect(() => { loadPoojas(); }, [loadPoojas]);

  const paginatedData = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setFormData({ ...DEFAULT_FORM });
    setEditingRow(null);
    setSelectedImageFile(null);
    setImagePreview('');
    onOpen();
  };

  const parseExcludedDates = (input) => {
    const raw = Array.isArray(input) ? input : String(input || '').split(/[\n,]+/);
    return raw
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.slice(0, 10));
  };

  const handleAddExcludedDate = () => {
    const date = formData.availability.excludedDateInput;
    if (!date) return;
    setFormData(prev => {
      const existing = prev.availability.excludedDates || [];
      if (existing.includes(date)) return prev;
      return {
        ...prev,
        availability: {
          ...prev.availability,
          excludedDates: [...existing, date],
          excludedDateInput: '',
        },
      };
    });
  };

  const handleRemoveExcludedDate = (index) => {
    setFormData(prev => {
      const next = (prev.availability.excludedDates || []).filter((_, idx) => idx !== index);
      return { ...prev, availability: { ...prev.availability, excludedDates: next } };
    });
  };

  const handleAddSpecialDate = () => {
    const date = formData.availability.specialDateInput;
    if (!date) return;
    setFormData(prev => {
      const existing = prev.availability.specialDates || [];
      if (existing.includes(date)) return prev;
      return {
        ...prev,
        availability: {
          ...prev.availability,
          specialDates: [...existing, date],
          specialDateInput: '',
        },
      };
    });
  };

  const handleRemoveSpecialDate = (index) => {
    setFormData(prev => {
      const next = (prev.availability.specialDates || []).filter((_, idx) => idx !== index);
      return { ...prev, availability: { ...prev.availability, specialDates: next } };
    });
  };

  const handleOpenEdit = (row) => {
    setEditingRow(row);

    // Build price + member fields generically so new built-in types fall back cleanly
    const priceDefaults = {};
    PRICE_FIELDS.forEach(p => { priceDefaults[p.field] = row[p.field] || 0; });

    const memberDefaults = {};
    MEMBER_FIELDS.forEach(m => {
      memberDefaults[m.field] = row[m.field] || DEFAULT_FORM[m.field];
    });

    const availability = row.availability || DEFAULT_AVAILABILITY;

    setFormData({
      poojaName:    row.poojaName    || '',
      description:  row.description  || '',
      ...priceDefaults,
      homamName:    row.homamName    || '',
      // Checkboxes only ever reflect the 6 built-in types — custom keys
      // saved into bookingTypes previously are represented via customTypes.
      bookingTypes: (row.bookingTypes || ['INDIVIDUAL', 'FAMILY']).filter(t => BOOKING_TYPES.includes(t)),
      ...memberDefaults,
      durationMinutes: row.durationMinutes || 60,
      imageUrl:     row.imageUrl     || '',
      benefits:     row.benefits     || [],
      poojaItems:   row.poojaItems   || [],
      customTypes:  row.customTypes  || [],
      noteText:     row.noteText     || '',
      availability: {
        ...DEFAULT_AVAILABILITY,
        ...availability,
        weeklyDays: Array.isArray(availability.weeklyDays) ? availability.weeklyDays : [],
        monthlyWeeks: Array.isArray(availability.monthlyWeeks) ? availability.monthlyWeeks : [1],
        monthlyWeekdays: Array.isArray(availability.monthlyWeekdays) ? availability.monthlyWeekdays : [1],
        specialDates: Array.isArray(availability.specialDates) ? availability.specialDates : [],
        specialDateInput: '',
        excludedDates: Array.isArray(availability.excludedDates) ? availability.excludedDates : (availability.excludedDates ? [availability.excludedDates] : []),
        excludedDateInput: '',
      },
      isActive:     row.isActive     ?? true,
    });
    setSelectedImageFile(null);
    setImagePreview(row.imageUrl || '');
    onOpen();
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // ── Toggle a built-in booking type on/off, enforcing exclusivity for
  //    WHOLE_PACKAGE: turning it on clears every other type (including
  //    custom types); turning a normal type on while an exclusive type is
  //    active drops the exclusive type first.
  const handleToggleBookingType = (type) => {
    setFormData(prev => {
      const existing = prev.bookingTypes || [];
      const isCurrentlyOn = existing.includes(type);

      if (isCurrentlyOn) {
        return { ...prev, bookingTypes: existing.filter(t => t !== type) };
      }

      if (EXCLUSIVE_TYPES.includes(type)) {
        return { ...prev, bookingTypes: [type], customTypes: [] };
      }

      const withoutExclusive = existing.filter(t => !EXCLUSIVE_TYPES.includes(t));
      return { ...prev, bookingTypes: [...withoutExclusive, type] };
    });
  };

  // ── Custom booking type helpers (per-pooja) ─────────────────────────────
  const genCustomKey = () => `CUSTOM_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  const handleAddCustomType = () => {
    setFormData(prev => ({
      ...prev,
      customTypes: [...(prev.customTypes || []), { key: genCustomKey(), label: '', price: 0, maxMembers: 10 }],
    }));
  };

  const handleUpdateCustomType = (key, field, value) => {
    setFormData(prev => ({
      ...prev,
      customTypes: (prev.customTypes || []).map(c => c.key === key ? { ...c, [field]: value } : c),
    }));
  };

  const handleRemoveCustomType = (key) => {
    setFormData(prev => ({ ...prev, customTypes: (prev.customTypes || []).filter(c => c.key !== key) }));
  };

  const handleAddBenefit = () => {
    if (!newBenefit.trim()) return;
    setFormData(prev => ({ ...prev, benefits: [...(prev.benefits || []), newBenefit.trim()] }));
    setNewBenefit('');
  };
  const handleRemoveBenefit = (i) =>
    setFormData(prev => ({ ...prev, benefits: prev.benefits.filter((_, idx) => idx !== i) }));

  const handleAddItem = () => {
    if (!newItem.trim()) return;
    setFormData(prev => ({ ...prev, poojaItems: [...(prev.poojaItems || []), newItem.trim()] }));
    setNewItem('');
  };
  const handleRemoveItem = (i) =>
    setFormData(prev => ({ ...prev, poojaItems: prev.poojaItems.filter((_, idx) => idx !== i) }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.poojaName) {
      toast({ title: 'Please enter Pooja Name', status: 'warning', duration: 3000 });
      return;
    }

    if ((formData.customTypes || []).some(c => !c.label.trim())) {
      toast({ title: 'Please name every custom booking type, or remove empty ones', status: 'warning', duration: 3000 });
      return;
    }
    const customTypesClean = (formData.customTypes || []).map(c => ({ ...c, label: c.label.trim() }));
    const allBookingTypes = [...(formData.bookingTypes || []), ...customTypesClean.map(c => c.key)];

    if (allBookingTypes.length === 0) {
      toast({ title: 'Select at least one booking type', status: 'warning', duration: 3000 });
      return;
    }

    // Validate: at least one enabled type (built-in or custom) must have a price > 0
    const hasPrice =
      (formData.bookingTypes || []).some(t => {
        const pf = PRICE_FIELDS.find(p => p.type === t);
        return pf && formData[pf.field] > 0;
      }) || customTypesClean.some(c => Number(c.price) > 0);

    if (!hasPrice) {
      toast({ title: 'Set a price > 0 for at least one selected booking type', status: 'warning', duration: 3000 });
      return;
    }

    // HOMAM requires a name
    if (formData.bookingTypes.includes('HOMAM') && !formData.homamName.trim()) {
      toast({ title: 'Please enter the Homam name', status: 'warning', duration: 3000 });
      return;
    }

    const availability = {
      ...DEFAULT_AVAILABILITY,
      ...formData.availability,
      excludedDates: parseExcludedDates(formData.availability?.excludedDates),
      specialDates: Array.isArray(formData.availability?.specialDates) ? formData.availability.specialDates : [],
    };

    if (availability.type === 'weekly' && (!availability.weeklyDays || availability.weeklyDays.length === 0)) {
      toast({ title: 'Please select at least one weekday for weekly availability', status: 'warning', duration: 3000 });
      return;
    }

    if (availability.type === 'monthly') {
      if (!availability.monthlyWeeks?.length || !availability.monthlyWeekdays?.length) {
        toast({ title: 'Please select at least one week and one weekday', status: 'warning', duration: 3000 });
        return;
      }
    }

    if (availability.type === 'special' && (!availability.specialDates || availability.specialDates.length === 0)) {
      toast({ title: 'Please add at least one special booking date', status: 'warning', duration: 3000 });
      return;
    }

    const saveData = {
      ...formData,
      // bookingTypes persisted includes both built-in keys and this pooja's
      // custom type keys, so the customer form can iterate one array.
      bookingTypes: allBookingTypes,
      customTypes: customTypesClean,
      availability,
    };

    setImageUploading(true);
    let result;

    if (editingRow) {
      const { id: _id, ...formWithoutId } = saveData;
      const updateData = { ...formWithoutId };
      if (editingRow.poojaId) updateData.poojaId = editingRow.poojaId;
      Object.keys(updateData).forEach(k => { if (updateData[k] === undefined) delete updateData[k]; });
      result = await updatePooja(editingRow.id, updateData, selectedImageFile);
    } else {
      result = await createPooja(saveData, selectedImageFile);
    }

    setImageUploading(false);

    if (result.success) {
      toast({ title: editingRow ? 'Pooja updated!' : 'Pooja created!', status: 'success', duration: 3000 });
      onClose();
      loadPoojas();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 4000 });
    }
  };

  // ── Toggle active / delete ────────────────────────────────────────────────
  const handleToggleStatus = async (row) => {
    const result = await togglePoojaStatus(row.id, !row.isActive);
    if (result.success) {
      toast({ title: `Pooja ${!row.isActive ? 'activated' : 'deactivated'}`, status: 'success', duration: 2000 });
      loadPoojas();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 3000 });
    }
  };

  const handleDeleteConfirm = (id) => { setDeletingId(id); onDeleteOpen(); };
  const handleDelete = async () => {
    const result = await deletePooja(deletingId);
    if (result.success) {
      toast({ title: 'Pooja deleted', status: 'success', duration: 2000 });
      loadPoojas();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 3000 });
    }
    onDeleteClose();
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    columnHelper.accessor('imageUrl', {
      header: 'Image',
      cell: info => (
        <Box w="50px" h="50px" borderRadius="8px" overflow="hidden" bg="gray.100">
          {info.getValue() ? (
            <Image src={info.getValue()} alt="pooja" w="50px" h="50px" objectFit="cover" />
          ) : (
            <Flex w="50px" h="50px" align="center" justify="center">
              <Icon as={MdImage} color="gray.400" w="24px" h="24px" />
            </Flex>
          )}
        </Box>
      ),
    }),
    columnHelper.accessor('poojaName', {
      header: 'Name',
      cell: info => <Text fontWeight="600" fontSize="sm" color={textColor}>{info.getValue()}</Text>,
    }),
    // Show prices only for enabled types (built-in + custom) — collapsed into one cell
    columnHelper.display({
      id: 'prices',
      header: 'Prices',
      cell: info => {
        const row = info.row.original;
        const types = row.bookingTypes || [];
        return (
          <VStack align="start" spacing={0}>
            {PRICE_FIELDS.filter(p => types.includes(p.type)).map(p => (
              <Text key={p.type} fontSize="xs" color={`${TYPE_COLOR[p.type]}.500`} fontWeight="700">
                {p.type}: ₹{row[p.field] || 0}
              </Text>
            ))}
            {(row.customTypes || []).map(c => (
              <Text key={c.key} fontSize="xs" color="gray.600" fontWeight="700">
                {c.label}: ₹{c.price || 0}
              </Text>
            ))}
          </VStack>
        );
      },
    }),
    columnHelper.accessor('durationMinutes', {
      header: 'Duration',
      cell: info => <Text fontSize="sm">{info.getValue()} min</Text>,
    }),
    columnHelper.accessor('bookingTypes', {
      header: 'Types',
      cell: info => {
        const row = info.row.original;
        const fixed = (info.getValue() || []).filter(t => BOOKING_TYPES.includes(t));
        return (
          <Wrap spacing={1}>
            {fixed.map(t => (
              <WrapItem key={t}>
                <Badge colorScheme={TYPE_COLOR[t]} fontSize="xs">{t}</Badge>
              </WrapItem>
            ))}
            {(row.customTypes || []).map(c => (
              <WrapItem key={c.key}>
                <Badge colorScheme="gray" fontSize="xs">{c.label}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        );
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      cell: info => (
        <Switch
          isChecked={info.getValue()}
          colorScheme="green"
          onChange={() => handleToggleStatus(info.row.original)}
          size="md"
        />
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: info => (
        <HStack spacing={2}>
          <IconButton icon={<Icon as={MdEdit} />} size="sm" colorScheme="blue" variant="ghost"
            aria-label="Edit" onClick={() => handleOpenEdit(info.row.original)} />
          <IconButton icon={<Icon as={MdDelete} />} size="sm" colorScheme="red" variant="ghost"
            aria-label="Delete" onClick={() => handleDeleteConfirm(info.row.original.id)} />
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // ── Whether HOMAM is selected ─────────────────────────────────────────────
  const homamSelected = (formData.bookingTypes || []).includes('HOMAM');

  // ── Whether an exclusive type (WHOLE_PACKAGE) is currently active —
  //    used to grey out / disable the other checkboxes and the custom-type
  //    "Add" button.
  const exclusiveActive = (formData.bookingTypes || []).some(t => EXCLUSIVE_TYPES.includes(t));

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX="auto">

      {/* Header */}
      <Flex px="25px" py="15px" justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Text color={textColor} fontSize="22px" fontWeight="700">Pooja Management</Text>
        <HStack spacing={3} flexWrap="wrap">
          <InputGroup w="220px">
            <Input
              placeholder="Search poojas..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              size="sm" borderRadius="8px"
            />
            <InputRightElement pointerEvents="none" h="32px">
              <Icon as={MdSearch} color="gray.400" />
            </InputRightElement>
          </InputGroup>
          <Select size="sm" borderRadius="8px" w="140px" value={filterActive}
            onChange={e => { setFilterActive(e.target.value); setCurrentPage(1); }}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <Button leftIcon={<Icon as={MdAdd} />} colorScheme="brand" size="sm"
            borderRadius="8px" onClick={handleOpenCreate}>
            Add Pooja
          </Button>
        </HStack>
      </Flex>

      {/* Table */}
      {loading ? (
        <Flex justify="center" align="center" py="60px"><Spinner size="lg" color="brand.500" /></Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" color="gray.500">
            <Thead>
              {table.getHeaderGroups().map(hg => (
                <Tr key={hg.id}>
                  {hg.headers.map(h => (
                    <Th key={h.id} borderColor={borderColor} pe="10px" ps="25px" py="12px">
                      <Flex align="center" fontSize="xs" fontWeight="600" color="gray.400">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" py="40px">
                    <Text color="gray.400">No poojas found</Text>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id} borderColor={borderColor} ps="25px" pe="10px" py="14px">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      <Flex px="25px" py="15px" justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">
          Showing {Math.min((currentPage - 1) * pageSize + 1, data.length)}–{Math.min(currentPage * pageSize, data.length)} of {data.length}
        </Text>
        <HStack>
          <IconButton icon={<Icon as={IoChevronBack} />} size="sm" variant="outline"
            isDisabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Previous" />
          <Text fontSize="sm" fontWeight="600">{currentPage} / {totalPages}</Text>
          <IconButton icon={<Icon as={IoChevronForward} />} size="sm" variant="outline"
            isDisabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Next" />
        </HStack>
      </Flex>

      {/* ── Create / Edit Modal ───────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRow ? 'Edit Pooja' : 'Create New Pooja'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">

              {/* Image Upload */}
              <FormControl>
                <FormLabel fontSize="sm">Pooja Image</FormLabel>
                <Flex w="100%" h="160px" border="2px dashed"
                  borderColor={imagePreview ? 'brand.500' : borderColor}
                  borderRadius="12px" align="center" justify="center"
                  cursor="pointer" overflow="hidden"
                  onClick={() => fileInputRef.current?.click()} position="relative">
                  {imagePreview ? (
                    <Image src={imagePreview} alt="Preview" objectFit="cover" w="100%" h="100%" />
                  ) : (
                    <VStack spacing={2}>
                      <Icon as={MdCameraAlt} w="32px" h="32px" color="gray.400" />
                      <Text fontSize="sm" color="gray.400">Click to upload image</Text>
                    </VStack>
                  )}
                  <Input type="file" accept="image/*" ref={fileInputRef} display="none" onChange={handleImageSelect} />
                </Flex>
              </FormControl>

              {/* Pooja Name */}
              <FormControl isRequired>
                <FormLabel fontSize="sm">Pooja Name</FormLabel>
                <Input
                  placeholder="e.g. Ganapathi Pooja"
                  value={formData.poojaName}
                  onChange={e => setFormData(p => ({ ...p, poojaName: e.target.value }))}
                />
              </FormControl>

              {/* Description — full rich text: headers, numeric px size, color,
                  background, bold/italic/underline/strike, sub/superscript,
                  lists, indent, align, blockquote, code block, link */}
              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Box border="1px solid" borderColor={borderColor} borderRadius="8px" overflow="hidden"
                  sx={{
                    '.ql-toolbar': { borderTop: 'none', borderLeft: 'none', borderRight: 'none', bg: 'gray.50', flexWrap: 'wrap' },
                    '.ql-container': { border: 'none', fontSize: '14px', minHeight: '160px' },
                    '.ql-editor': { minHeight: '160px' },
                    // Show plain numbers (e.g. "18") instead of "18px" in the size dropdown
                    '.ql-picker.ql-size .ql-picker-label::before': { content: 'attr(data-value)' },
                    '.ql-picker.ql-size .ql-picker-item::before': { content: 'attr(data-value)' },
                  }}>
                  <ReactQuill
                    value={formData.description}
                    onChange={val => setFormData(p => ({ ...p, description: val }))}
                    modules={QUILL_TOOLBAR_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Pooja description..."
                    theme="snow"
                  />
                </Box>
              </FormControl>

              {/* ── Booking Types (built-in) ───────────────────────────────── */}
              <FormControl>
                <FormLabel fontSize="sm">Booking Types</FormLabel>
                <Wrap spacing={4}>
                  {BOOKING_TYPES.map(type => {
                    const isChecked = (formData.bookingTypes || []).includes(type);
                    // Disable every other checkbox while an exclusive type is active
                    const isDisabled = exclusiveActive && !isChecked;
                    return (
                      <WrapItem key={type}>
                        <Checkbox
                          isChecked={isChecked}
                          isDisabled={isDisabled}
                          onChange={() => handleToggleBookingType(type)}
                          colorScheme={TYPE_COLOR[type]}
                        >
                          <Badge
                            colorScheme={TYPE_COLOR[type]}
                            fontSize="xs"
                            px={2} py={1}
                            borderRadius="md"
                            opacity={isDisabled ? 0.4 : 1}
                          >
                            {formatTypeLabel(type)}
                          </Badge>
                        </Checkbox>
                      </WrapItem>
                    );
                  })}
                </Wrap>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Only selected types will be shown to devotees on the website.
                  {' '}Whole Package is standalone — picking it clears any other selection, including custom types.
                </Text>
              </FormControl>

              {/* ── Custom Booking Types (per-pooja) ─────────────────────────── */}
              <FormControl>
                <FormLabel fontSize="sm">Custom Booking Types</FormLabel>
                <VStack spacing={3} align="stretch">
                  {(formData.customTypes || []).map(c => (
                    <Flex key={c.key} gap={3} align="flex-end" wrap="wrap" p={3}
                      border="1px solid" borderColor={borderColor} borderRadius="10px">
                      <Box flex="2" minW="140px">
                        <Text fontSize="xs" color="gray.500" mb={1}>Type Name</Text>
                        <Input size="sm" placeholder="e.g. VIP Darshan" value={c.label}
                          isDisabled={exclusiveActive}
                          onChange={e => handleUpdateCustomType(c.key, 'label', e.target.value)} />
                      </Box>
                      <Box flex="1" minW="110px">
                        <Text fontSize="xs" color="gray.500" mb={1}>Price (₹)</Text>
                        <NumberInput size="sm" min={0} value={c.price} isDisabled={exclusiveActive}
                          onChange={val => handleUpdateCustomType(c.key, 'price', Number(val))}>
                          <NumberInputField />
                        </NumberInput>
                      </Box>
                      <Box flex="1" minW="110px">
                        <Text fontSize="xs" color="gray.500" mb={1}>Max Members</Text>
                        <NumberInput size="sm" min={1} value={c.maxMembers} isDisabled={exclusiveActive}
                          onChange={val => handleUpdateCustomType(c.key, 'maxMembers', Number(val))}>
                          <NumberInputField />
                        </NumberInput>
                      </Box>
                      <IconButton icon={<Icon as={MdDelete} />} size="sm" colorScheme="red" variant="ghost"
                        aria-label="Remove custom type" onClick={() => handleRemoveCustomType(c.key)} />
                    </Flex>
                  ))}
                </VStack>
                <Button mt={3} size="sm" leftIcon={<Icon as={MdAdd} />} variant="outline" colorScheme="brand"
                  isDisabled={exclusiveActive} onClick={handleAddCustomType}>
                  Add Custom Type
                </Button>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Custom types are specific to this pooja and appear alongside the standard types above.
                </Text>
              </FormControl>

              {/* ── Prices — one row per ENABLED built-in type ───────────── */}
              <FormControl>
                <FormLabel fontSize="sm">Prices per Type</FormLabel>
                <Flex gap={4} wrap="wrap">
                  {PRICE_FIELDS.filter(p => (formData.bookingTypes || []).includes(p.type)).map(p => (
                    <Box key={p.type} flex="1" minW="160px">
                      <Text fontSize="xs" mb={1} color="gray.500">
                        <Badge colorScheme={TYPE_COLOR[p.type]} fontSize="xs" mr={1}>{formatTypeLabel(p.type)}</Badge>
                        {p.label}
                      </Text>
                      <NumberInput
                        min={0}
                        value={formData[p.field]}
                        onChange={val => setFormData(prev => ({ ...prev, [p.field]: Number(val) }))}
                      >
                        <NumberInputField placeholder="0" />
                        <NumberInputStepper>
                          <NumberIncrementStepper /><NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Box>
                  ))}
                </Flex>
                {(formData.bookingTypes || []).length === 0 && (formData.customTypes || []).length === 0 && (
                  <Text fontSize="xs" color="orange.400" mt={1}>Select at least one booking type above.</Text>
                )}
              </FormControl>

              {/* ── Member counts — one row per ENABLED built-in type ────── */}
              <FormControl>
                <FormLabel fontSize="sm">Max Members per Type</FormLabel>
                <Flex gap={4} wrap="wrap">
                  {MEMBER_FIELDS.filter(m => (formData.bookingTypes || []).includes(m.type)).map(m => (
                    <Box key={m.type} flex="1" minW="160px">
                      <Text fontSize="xs" mb={1} color="gray.500">
                        <Badge colorScheme={TYPE_COLOR[m.type]} fontSize="xs" mr={1}>{formatTypeLabel(m.type)}</Badge>
                        {m.label}
                      </Text>
                      <NumberInput
                        min={1}
                        max={m.max}
                        value={formData[m.field]}
                        onChange={val => setFormData(prev => ({ ...prev, [m.field]: Number(val) }))}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper /><NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Box>
                  ))}
                </Flex>
              </FormControl>

              {/* ── Homam Name (shown only when HOMAM is selected) ─────────── */}
              {homamSelected && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Homam Name</FormLabel>
                  <Input
                    placeholder="e.g. Ganapathi Homam, Navagraha Homam..."
                    value={formData.homamName}
                    onChange={e => setFormData(p => ({ ...p, homamName: e.target.value }))}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    This name will be displayed on the website for the Homam booking type.
                  </Text>
                </FormControl>
              )}

              {/* Duration */}
              <FormControl>
                <FormLabel fontSize="sm">Duration (min)</FormLabel>
                <NumberInput min={15} value={formData.durationMinutes}
                  onChange={val => setFormData(p => ({ ...p, durationMinutes: Number(val) }))}>
                  <NumberInputField />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
              </FormControl>

              {/* Availability Schedule */}
              <Box border="1px solid" borderColor={borderColor} borderRadius="16px" p={4} bg="gray.50">
                <Text fontSize="sm" fontWeight="700" mb={3}>Availability Schedule</Text>
                <Flex gap={4} wrap="wrap">
                  <FormControl flex="1">
                    <FormLabel fontSize="sm">Availability Type</FormLabel>
                    <Select
                      value={formData.availability.type}
                      onChange={e => setFormData(p => ({ ...p, availability: { ...p.availability, type: e.target.value } }))}
                    >
                      <option value="daily">All Day</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="special">Special Dates</option>
                    </Select>
                  </FormControl>
                </Flex>

                {formData.availability.type === 'weekly' && (
                  <Box mt={4}>
                    <FormLabel fontSize="sm">Weekly Days</FormLabel>
                    <Wrap spacing={2}>
                      {WEEKDAYS.map((day, index) => (
                        <WrapItem key={day}>
                          <Button
                            size="sm"
                            variant={formData.availability.weeklyDays?.includes(index) ? 'solid' : 'outline'}
                            colorScheme="brand"
                            onClick={() => {
                              setFormData(prev => {
                                const selected = prev.availability.weeklyDays || [];
                                const next = selected.includes(index)
                                  ? selected.filter(d => d !== index)
                                  : [...selected, index];
                                return { ...prev, availability: { ...prev.availability, weeklyDays: next } };
                              });
                            }}
                          >
                            {day.slice(0, 3)}
                          </Button>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Select one or more weekdays for weekly availability.
                    </Text>
                  </Box>
                )}

                {formData.availability.type === 'monthly' && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="600" mb={2}>Monthly Configuration</Text>
                    <Text fontSize="xs" color="gray.500" mb={4}>
                      Select one or more weeks of the month and weekdays when this pooja is available.
                    </Text>

                    <FormLabel fontSize="sm">Weeks of Month</FormLabel>
                    <Wrap spacing={2} mb={4}>
                      {[1, 2, 3, 4, 5].map(week => (
                        <WrapItem key={week}>
                          <Button
                            size="sm"
                            variant={formData.availability.monthlyWeeks?.includes(week) ? 'solid' : 'outline'}
                            colorScheme="brand"
                            onClick={() => {
                              setFormData(prev => {
                                const selected = prev.availability.monthlyWeeks || [];
                                const next = selected.includes(week)
                                  ? selected.filter(w => w !== week)
                                  : [...selected, week];
                                return { ...prev, availability: { ...prev.availability, monthlyWeeks: next } };
                              });
                            }}
                          >
                            {week}st
                          </Button>
                        </WrapItem>
                      ))}
                    </Wrap>

                    <FormLabel fontSize="sm">Weekdays</FormLabel>
                    <Wrap spacing={2}>
                      {WEEKDAYS.map((day, index) => (
                        <WrapItem key={day}>
                          <Button
                            size="sm"
                            variant={formData.availability.monthlyWeekdays?.includes(index) ? 'solid' : 'outline'}
                            colorScheme="brand"
                            onClick={() => {
                              setFormData(prev => {
                                const selected = prev.availability.monthlyWeekdays || [];
                                const next = selected.includes(index)
                                  ? selected.filter(d => d !== index)
                                  : [...selected, index];
                                return { ...prev, availability: { ...prev.availability, monthlyWeekdays: next } };
                              });
                            }}
                          >
                            {day.slice(0, 3)}
                          </Button>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}

                {formData.availability.type === 'special' && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="600" mb={2}>Special Booking Dates</Text>
                    <Flex gap={2} wrap="wrap" alignItems="flex-end">
                      <FormControl flex="1" minW="220px">
                        <FormLabel fontSize="sm">Add Special Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.availability.specialDateInput || ''}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            availability: { ...prev.availability, specialDateInput: e.target.value },
                          }))}
                        />
                      </FormControl>
                      <Button mt="24px" size="sm" colorScheme="brand" onClick={handleAddSpecialDate}>
                        Add Date
                      </Button>
                    </Flex>
                    <Wrap spacing={2} mt={3}>
                      {(formData.availability.specialDates || []).map((date, i) => (
                        <WrapItem key={date + String(i)}>
                          <Tag size="md" colorScheme="purple" borderRadius="full">
                            <TagLabel>{date}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveSpecialDate(i)} />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Special dates are used exactly as selected, without weekly/monthly recurrence.
                    </Text>
                  </Box>
                )}

                <Flex gap={4} wrap="wrap" mt={4}>
                  <FormControl flex="1">
                    <FormLabel fontSize="sm">Max Bookings per Date</FormLabel>
                    <NumberInput
                      min={1}
                      value={formData.availability.maxBookingsPerDate}
                      onChange={val => setFormData(p => ({ ...p, availability: { ...p.availability, maxBookingsPerDate: Number(val) } }))}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper /><NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </Flex>

                <FormControl mt={4}>
                  <FormLabel fontSize="sm">Excluded / Holiday Dates</FormLabel>
                  <Flex gap={2} wrap="wrap" alignItems="flex-end">
                    <FormControl flex="1" minW="220px">
                      <Input
                        type="date"
                        value={formData.availability.excludedDateInput || ''}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          availability: { ...prev.availability, excludedDateInput: e.target.value },
                        }))}
                      />
                    </FormControl>
                    <Button mt="24px" size="sm" colorScheme="brand" onClick={handleAddExcludedDate}>
                      Add Date
                    </Button>
                  </Flex>
                  <Wrap spacing={2} mt={3}>
                    {(formData.availability.excludedDates || []).map((date, i) => (
                      <WrapItem key={date + String(i)}>
                        <Tag size="md" colorScheme="red" borderRadius="full">
                          <TagLabel>{date}</TagLabel>
                          <TagCloseButton onClick={() => handleRemoveExcludedDate(i)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    These dates will be removed from the available booking schedule.
                  </Text>
                </FormControl>
              </Box>

              {/* Benefits */}
              <FormControl>
                <FormLabel fontSize="sm">Benefits</FormLabel>
                <Wrap mb={2} spacing={2}>
                  {(formData.benefits || []).map((b, i) => (
                    <WrapItem key={i}>
                      <Tag size="md" colorScheme="green" borderRadius="full">
                        <TagLabel>{b}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveBenefit(i)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <HStack>
                  <Input placeholder="Add benefit (e.g., Prosperity, Health)..." value={newBenefit}
                    onChange={e => setNewBenefit(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddBenefit()} size="sm" />
                  <Button size="sm" colorScheme="green" onClick={handleAddBenefit}>Add</Button>
                </HStack>
              </FormControl>

              {/* Pooja Items */}
              <FormControl>
                <FormLabel fontSize="sm">Pooja Items Required</FormLabel>
                <Wrap mb={2} spacing={2}>
                  {(formData.poojaItems || []).map((item, i) => (
                    <WrapItem key={i}>
                      <Tag size="md" colorScheme="orange" borderRadius="full">
                        <TagLabel>{item}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveItem(i)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <HStack>
                  <Input placeholder="Add item (e.g., Coconut, Flowers)..." value={newItem}
                    onChange={e => setNewItem(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddItem()} size="sm" />
                  <Button size="sm" colorScheme="orange" onClick={handleAddItem}>Add</Button>
                </HStack>
              </FormControl>

              {/* ── Info Note shown on the customer booking page ─────────────── */}
              <FormControl>
                <FormLabel fontSize="sm">Info Note (shown to devotees on booking page)</FormLabel>
                <Textarea
                  placeholder="Optional note shown above the date picker — e.g. Homam participation details, prasadam info, which types this pooja can be booked under, etc."
                  value={formData.noteText}
                  onChange={e => setFormData(p => ({ ...p, noteText: e.target.value }))}
                  rows={4}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Leave blank to hide the note entirely on the booking page.
                </Text>
              </FormControl>

              {/* Active Toggle */}
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="active-toggle" mb="0" fontSize="sm">Active Status</FormLabel>
                <Switch id="active-toggle" isChecked={formData.isActive} colorScheme="green"
                  onChange={e => setFormData(p => ({ ...p, isActive: e.target.checked }))} />
              </FormControl>
              <Text fontSize="xs" color="gray.500" mt={-2}>
                Inactive poojas will not appear in booking forms.
              </Text>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSave} isLoading={imageUploading}
              loadingText={selectedImageFile ? 'Uploading image...' : 'Saving...'}>
              {editingRow ? 'Update Pooja' : 'Create Pooja'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Pooja</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete this pooja? This action cannot be undone.</Text>
            <Text fontSize="sm" color="red.500" mt={3}>
              Warning: This will also delete all associated bookings and records.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete Permanently</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Card>
  );
}